import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import CopyButton from "@/components/CopyButton";
import NotificationSettingsCard from "@/components/NotificationSettingsCard";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import Layer11SettingsCard from "@/components/settings/Layer11SettingsCard";
import AiMemoryCard from "@/components/AiMemoryCard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeProvider";
import { Sun, Moon, Copy, Download, Pencil } from "lucide-react";
import InfoTooltip from "@/components/InfoTooltip";
import AuditLogView from "@/components/AuditLogView";
import UserAvatar from "@/components/UserAvatar";
import ProfileEditModal from "@/components/ProfileEditModal";
import PageHeader from "@/components/dashboard/PageHeader";
import PreferencesCard from "@/components/settings/PreferencesCard";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";

export default function Settings() {
    const { user, refresh } = useAuth();
    const { theme, toggle } = useTheme();
    const [optOut, setOptOut] = useState(!!user?.leaderboard_opt_out);
    const [busy, setBusy] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Deep-link from the "Get set up" profile-completion widget / Sidebar
    // nudge — "Update profile" links to /app/settings?edit_profile=1 so the
    // edit modal (with its placeholders) is immediately visible instead of
    // requiring the user to spot the small pencil icon on the profile card.
    useEffect(() => {
        if (searchParams.get("edit_profile") === "1") {
            setEditOpen(true);
            searchParams.delete("edit_profile");
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Obondo memory (AiMemoryCard) is an institutional-only feature — hidden
    // from individual accounts entirely.
    const isInstitutional = Boolean(
        user?.role === "broker"
        || user?.tier_source === "agency_bundled"
        || user?.agency_id
        || user?.tier === "advanced"
        || user?.tier === "moe_institution"
        || user?.tier === "cma_institution"
    );

    useEffect(() => { setOptOut(!!user?.leaderboard_opt_out); }, [user]);

    async function saveOptOut(v) {
        setOptOut(v);
        setBusy(true);
        try {
            await api.post("/privacy/leaderboard-opt-out", { leaderboard_opt_out: v });
            await refresh();
            toast.success(v ? "Opted out — hidden from leaderboard" : "Now visible on leaderboard");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    }

    const refLink = `${window.location.origin}/register?ref=${user?.referral_code}`;
    const shareLink = `${process.env.REACT_APP_BACKEND_URL}/api/r/${user?.referral_code}`;
    const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/referral/og-card/${user?.referral_code}.png`;

    // Fire-and-forget audit-log hook. Runs the moment a user CLICKS a share button
    // (right before the target social network opens in a new tab). Failures are
    // silent — telemetry must never block the share intent.
    const trackShare = (channel) => {
        try { api.post("/referral/share", { channel }); } catch { /* noop */ }
    };

    const defaultCardIds = useMemo(() => {
        const ids = ["profile", "prefs", "theme", "password", "layer11"];
        if (user?.role !== "admin") ids.push("referral");
        ids.push("privacy", "notifications");
        if (isInstitutional) ids.push("ai-memory");
        return ids;
    }, [user?.role, isInstitutional]);

    const SETTINGS_WIDGET_META = {
        profile:       { title: "Profile",               layout: { x: 0, y: 0,  w: 4, h: 6  }, minW: 3, minH: 4 },
        prefs:         { title: "Preferences",            layout: { x: 4, y: 0,  w: 4, h: 9  }, minW: 3, minH: 5 },
        theme:         { title: "Appearance",             layout: { x: 8, y: 0,  w: 4, h: 4  }, minW: 3, minH: 3 },
        password:      { title: "Security",               layout: { x: 4, y: 9,  w: 4, h: 11 }, minW: 3, minH: 6 },
        layer11:       { title: "Layer 11 Verification",  layout: { x: 0, y: 6,  w: 4, h: 9  }, minW: 3, minH: 5 },
        referral:      { title: "Referral Program",       layout: { x: 8, y: 14, w: 4, h: 8  }, minW: 3, minH: 5 },
        privacy:       { title: "Privacy",                layout: { x: 0, y: 15, w: 4, h: 4  }, minW: 3, minH: 3 },
        notifications: { title: "Notifications",          layout: { x: 8, y: 4,  w: 4, h: 10 }, minW: 3, minH: 6 },
        "ai-memory":   { title: "Obondo Memory",           layout: { x: 8, y: 22, w: 4, h: 9  }, minW: 3, minH: 5 },
    };

    const widgets = defaultCardIds.map((id) => {
        const meta = SETTINGS_WIDGET_META[id] || { title: id, layout: { x: 0, y: 0, w: 4, h: 8 }, minW: 3, minH: 4 };
        return {
            id,
            title: meta.title,
            category: "Settings",
            layout: meta.layout,
            minW: meta.minW,
            minH: meta.minH,
            noPadding: true,
            render: () => renderSettingsCard(id),
        };
    });

    function renderSettingsCard(id) {
        switch (id) {
            case "profile":
                return (
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="settings-profile-card">
                        <div className="flex items-start gap-4">
                            <UserAvatar user={user} size={56} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="text-lg font-bold truncate">{user?.full_name || "—"}</div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setEditOpen(true)}
                                        className="h-6 px-2 text-xs shrink-0"
                                        data-testid="settings-edit-profile"
                                    >
                                        <Pencil className="w-3 h-3 mr-1" /> Edit profile
                                    </Button>
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    Tier: <span className="font-semibold text-foreground">{user?.tier || "free"}</span>
                                </div>
                                <div className="flex justify-between mt-2">
                                    <span className="text-xs text-muted-foreground">Account status</span>
                                    <span className={`text-xs font-semibold ${user?.account_status === "frozen" ? "text-danger" : "text-success"}`}>
                                        {user?.account_status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case "prefs":
                return <PreferencesCard />;
            case "theme":
                return (
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="settings-theme-card">
                        <div className="overline mb-3">Appearance</div>
                        <div className="flex items-center justify-between">
                            <div className="text-sm">
                                <div className="font-semibold">Dark mode</div>
                                <div className="text-xs text-muted-foreground">Toggle between light and dark themes.</div>
                            </div>
                            <button type="button" onClick={toggle} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="settings-theme-toggle">
                                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                );
            case "password":
                return <ChangePasswordCard />;
            case "layer11":
                return <Layer11SettingsCard />;
            case "referral":
                return (
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="settings-referral-card">
                        <div className="overline mb-3">Referral program</div>
                        <div className="text-xs text-muted-foreground mb-3">
                            Share your unique link. When someone registers through it, you both benefit.
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    readOnly
                                    value={refLink}
                                    className="flex-1 bg-muted/40 border border-border rounded px-2 py-1.5 text-xs font-mono truncate"
                                    data-testid="settings-referral-link"
                                />
                                <CopyButton value={refLink} toastMsg="Referral link copied" iconOnly testId="settings-copy-referral" />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <a
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Join me on Melian Dialogue!")}&url=${encodeURIComponent(shareLink)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    onClick={() => trackShare("twitter")}
                                    className="text-xs text-primary hover:underline"
                                    data-testid="settings-share-twitter"
                                >Share on X</a>
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent("Join me on Melian Dialogue! " + shareLink)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    onClick={() => trackShare("whatsapp")}
                                    className="text-xs text-primary hover:underline"
                                    data-testid="settings-share-whatsapp"
                                >WhatsApp</a>
                            </div>
                        </div>
                    </div>
                );
            case "privacy":
                return (
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="settings-privacy-card">
                        <div className="overline mb-3">Privacy</div>
                        <div className="flex items-center justify-between">
                            <div className="text-sm">
                                <div className="font-semibold">Hide from leaderboard</div>
                                <div className="text-xs text-muted-foreground">When enabled, your profile won't appear in public rankings.</div>
                            </div>
                            <Switch checked={optOut} onCheckedChange={saveOptOut} disabled={busy} data-testid="settings-leaderboard-opt-out" />
                        </div>
                    </div>
                );
            case "notifications":
                return <NotificationSettingsCard />;
            case "ai-memory":
                return <AiMemoryCard />;
            default:
                return null;
        }
    }

    return (
        <>
            <PageHeader
                eyebrow="Settings"
                title="Your account"
                subtitle="Profile, notification preferences, privacy, sessions, and your 90-day audit log. Drag, resize, minimize, or remove any widget — restore them anytime via Manage widgets."
            />

            <Tabs defaultValue="account">
                <TabsList className="w-full justify-start border-b border-border bg-transparent p-0 rounded-none h-auto mb-4">
                    <TabsTrigger value="account" data-testid="settings-tab-account" className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent">Account</TabsTrigger>
                    <TabsTrigger value="audit"   data-testid="settings-tab-audit"   className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent">Audit log</TabsTrigger>
                </TabsList>

                <TabsContent value="account">
                    <DashboardGridWrapper role="TRADER" viewId="settings" widgets={widgets} />

                    <ProfileEditModal open={editOpen} onClose={() => setEditOpen(false)} />
                </TabsContent>

                <TabsContent value="audit">
                    <div className="border border-border bg-card rounded p-4">
                        <div className="overline mb-1">Your activity — last 90 days</div>
                        <div className="text-xs text-muted-foreground mb-4">
                            Every login, manual trade, Obondo AI-initiated trade, working
                            order, and journal entry tied to your account. Available to
                            all tiers — Individual, MoE, and CMA Institutional. Filter by
                            window, download the raw feed as CSV, or print for compliance
                            records.
                        </div>
                        <AuditLogView
                            jsonPath="/me/audit-log"
                            csvPath="/me/audit-log.csv"
                            subject={user?.email || "audit"}
                            defaultDays={90}
                        />
                    </div>
                </TabsContent>
            </Tabs>
        </>
    );
}
